// ─────────────────────────────────────────────────────────────────────────────
// fridge-analysis-card.component.ts
// Análisis dual del espacio de nevera: Postobón (izq) vs Competencia (der).
// Muestra categorías, empresas, presentaciones y posición en nevera.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

// ── Tipos públicos ─────────────────────────────────────────────────────────

export interface FridgeSizeEntry   { size: string; count: number }
export interface FridgeBrandRow    { brand: string; sizes: Record<string, number>; total: number }

export interface FridgeSpacePanel {
  tag:          'postobon' | 'competencia';
  label:        string;
  accentColor:  string;
  totalFaces:   number;
  pctOfFridge:  number;   // % sobre total postobon+competencia
  avgScore:     number;

  categoryDist: { category: string; count: number; pct: number; color: string }[];
  companyDist:  { company: string;  count: number; pct: number; isAlert: boolean }[];
  brandRows:    FridgeBrandRow[];
  sizeColumns:  string[];           // columnas canónicas de presentación
  positionGrid: number[][];         // [3 rows][3 cols] — top/mid/bot × left/ctr/right
  gridMax:      number;

  pricingCount: { tipo: string; count: number }[];
}

export interface FridgeAnalysisCardConfig {
  title:            string;
  totalImages:      number;
  postobonPanel:    FridgeSpacePanel;
  competenciaPanel: FridgeSpacePanel;
  // Vacíos detectados como tag propio
  vaciosCount:      number;
  vaciosGrid:       number[][];
  vaciosGridMax:    number;
}

// ── Paleta de categorías (nombres reales de la BD) ─────────────────────────
const CAT_COLORS: Record<string, string> = {
  'Gaseosa':          '#6366f1',
  'Agua':             '#22d3ee',
  'Jugo':             '#4ade80',
  'Bebida Hidratante':'#38bdf8',
  'Bebida Energizante':'#f59e0b',
  'Cerveza':          '#fb923c',
  'Leche Saborizada': '#e879f9',
  'Suero':            '#a78bfa',
  'Sin categoria':    '#64748b',
  'Planimetria':      '#475569',
};

// ── Tamaños canónicos (orden de columnas en la matriz) ─────────────────────
export const CANONICAL_SIZES = ['Personal', '280ml', '350ml', '400ml', '600ml', '1000ml', '1500ml', '2000ml', '2500ml', '3000ml', 'Familiar'];

@Component({
  selector:        'app-fridge-analysis-card',
  standalone:      true,
  imports:         [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<div class="fa-wrap">

  <!-- Header -->
  <div class="fa-header">
    <span class="fa-title">{{ config?.title ?? 'Análisis de Nevera' }}</span>
    @if (loading) { <span class="fa-badge fa-badge--loading">Cargando…</span> }
    @else { <span class="fa-badge">{{ config?.totalImages ?? 0 }} imágenes analizadas</span> }
  </div>

  @if (loading) {
    <div class="fa-skeleton">
      <div class="fa-sk-panel"></div>
      <div class="fa-sk-panel"></div>
    </div>
  } @else if (config) {

    <!-- Alerta de contaminación -->
    @if (contaminationAlert > 30) {
      <div class="fa-alert fa-alert--danger">
        <i class="pi pi-exclamation-triangle"></i>
        <strong>Alerta:</strong> {{ contaminationAlert.toFixed(0) }}% del espacio Postobón está ocupado por marcas ajenas.
      </div>
    } @else if (contaminationAlert > 10) {
      <div class="fa-alert fa-alert--warn">
        <i class="pi pi-exclamation-circle"></i>
        <strong>Atención:</strong> {{ contaminationAlert.toFixed(0) }}% de contaminación en espacio Postobón.
      </div>
    }

    <!-- Paneles lado a lado -->
    <div class="fa-panels">
      @for (panel of [config.postobonPanel, config.competenciaPanel]; track panel.tag) {
        <div class="fa-panel" [class.fa-panel--postobon]="panel.tag === 'postobon'"
                              [class.fa-panel--competencia]="panel.tag === 'competencia'">

          <!-- Panel header -->
          <div class="fa-panel__head">
            <div class="fa-panel__label" [style.color]="panel.accentColor">
              <span class="fa-panel__dot" [style.background]="panel.accentColor"></span>
              {{ panel.label }}
            </div>
            <div class="fa-panel__kpis">
              <div class="fa-kpi">
                <span>Caras</span>
                <strong>{{ panel.totalFaces }}</strong>
              </div>
              <div class="fa-kpi">
                <span>% Nevera</span>
                <strong [style.color]="panel.tag === 'postobon' && panel.pctOfFridge < 50 ? '#ef4444' : panel.accentColor">
                  {{ panel.pctOfFridge.toFixed(0) }}%
                </strong>
              </div>
              <div class="fa-kpi">
                <span>Score</span>
                <strong>{{ (panel.avgScore * 100).toFixed(0) }}%</strong>
              </div>
            </div>
          </div>

          <!-- Distribución por empresa -->
          <div class="fa-section-title">Empresas en este espacio</div>
          <div class="fa-company-bars">
            @for (c of panel.companyDist; track c.company) {
              <div class="fa-company-row">
                <span class="fa-company-name"
                      [class.fa-company-name--alert]="c.isAlert">{{ c.company }}</span>
                <div class="fa-bar-track">
                  <div class="fa-bar-fill"
                       [style.width.%]="c.pct"
                       [style.background]="companyColor(c.company, panel.tag, c.isAlert)">
                  </div>
                </div>
                <span class="fa-bar-val" [class.fa-bar-val--alert]="c.isAlert">
                  {{ c.count }} <small>({{ c.pct.toFixed(0) }}%)</small>
                </span>
              </div>
            }
          </div>

          <!-- Distribución por categoría -->
          <div class="fa-section-title">Categorías</div>
          <div class="fa-category-list">
            @for (cat of panel.categoryDist; track cat.category) {
              <div class="fa-cat-row">
                <span class="fa-cat-dot" [style.background]="cat.color"></span>
                <span class="fa-cat-name">{{ cat.category }}</span>
                <div class="fa-cat-track">
                  <div class="fa-cat-fill"
                       [style.width.%]="cat.pct"
                       [style.background]="cat.color">
                  </div>
                </div>
                <span class="fa-cat-val">{{ cat.pct.toFixed(0) }}%</span>
                <span class="fa-cat-count">{{ cat.count }}</span>
              </div>
            }
          </div>

          <!-- Matriz de presentaciones -->
          @if (panel.brandRows.length) {
            <div class="fa-section-title">Presentaciones por marca</div>
            <div class="fa-matrix-wrap">
              <table class="fa-matrix">
                <thead>
                  <tr>
                    <th>Marca</th>
                    @for (sz of panel.sizeColumns; track sz) {
                      <th>{{ sz }}</th>
                    }
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of panel.brandRows; track row.brand) {
                    <tr>
                      <td class="fa-matrix__brand">{{ row.brand }}</td>
                      @for (sz of panel.sizeColumns; track sz) {
                        <td [class.fa-matrix__zero]="!row.sizes[sz]">
                          {{ row.sizes[sz] || '—' }}
                        </td>
                      }
                      <td class="fa-matrix__total">{{ row.total }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }

          <!-- Grid posición en nevera 3×3 -->
          <div class="fa-section-title">Posición en nevera</div>
          <div class="fa-grid-wrap">
            <div class="fa-grid-labels-col">
              <span>Arriba</span><span>Medio</span><span>Abajo</span>
            </div>
            <div class="fa-grid">
              @for (row of panel.positionGrid; track $index; let ri = $index) {
                @for (cell of row; track $index; let ci = $index) {
                  <div class="fa-grid-cell"
                       [style.background]="gridCellColor(cell, panel.gridMax, panel.accentColor)"
                       [title]="gridLabel(ri, ci) + ': ' + cell + ' productos'">
                    @if (cell > 0) { <span>{{ cell }}</span> }
                  </div>
                }
              }
            </div>
            <div class="fa-grid-labels-row">
              <span>Izq</span><span>Centro</span><span>Der</span>
            </div>
          </div>

          <!-- Comunicación / Precios -->
          @if (panel.pricingCount.length) {
            <div class="fa-section-title">Comunicación</div>
            <div class="fa-pricing-row">
              @for (p of panel.pricingCount; track p.tipo) {
                <div class="fa-pricing-chip">
                  <i class="pi" [class]="pricingIcon(p.tipo)"></i>
                  <span>{{ p.tipo }}</span>
                  <strong>{{ p.count }}</strong>
                </div>
              }
            </div>
          }

        </div>
      }
    </div>

    <!-- Sección vacíos -->
    @if (config.vaciosCount > 0) {
      <div class="fa-vacios-section">
        <div class="fa-vacios-header">
          <i class="pi pi-box"></i>
          <span class="fa-section-title" style="margin:0">Espacios vacíos detectados</span>
          <strong class="fa-vacios-count">{{ config.vaciosCount }} huecos</strong>
        </div>
        <div class="fa-vacios-body">
          <p class="fa-vacios-desc">
            El modelo detectó espacios sin producto. El mapa muestra su distribución en la nevera.
          </p>
          <div class="fa-grid-wrap">
            <div class="fa-grid">
              @for (row of config.vaciosGrid; track $index; let ri = $index) {
                @for (cell of row; track $index; let ci = $index) {
                  <div class="fa-grid-cell"
                       [style.background]="gridCellColor(cell, config.vaciosGridMax, '#f59e0b')"
                       [title]="gridLabel(ri, ci) + ': ' + cell + ' vacíos'">
                    @if (cell > 0) { <span>{{ cell }}</span> }
                  </div>
                }
              }
            </div>
            <div class="fa-grid-labels-row">
              <span>Izq</span><span>Centro</span><span>Der</span>
            </div>
          </div>
        </div>
      </div>
    }

  } @else {
    <div class="fa-empty">Sin datos para el período seleccionado.</div>
  }

</div>
  `,
  styles: [`
    :host { display: block; }

    .fa-wrap {
      background: #1a1d2e;
      border-radius: 16px;
      padding: 20px;
      color: #e2e8f0;
      font-family: inherit;
    }

    /* Header */
    .fa-header {
      display: flex; align-items: center; gap: 12px;
      margin-bottom: 14px;
    }
    .fa-title { font-size: 15px; font-weight: 600; color: #f1f5f9; flex: 1; }
    .fa-badge {
      font-size: 11px; background: #0f1120; color: #64748b;
      border-radius: 20px; padding: 3px 10px;
    }
    .fa-badge--loading { color: #6366f1; }

    /* Alerta */
    .fa-alert {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 14px; border-radius: 10px;
      font-size: 13px; margin-bottom: 14px;
    }
    .fa-alert--danger { background: rgba(239,68,68,.12); color: #fca5a5; border: 1px solid rgba(239,68,68,.3); }
    .fa-alert--warn   { background: rgba(245,158,11,.12); color: #fcd34d; border: 1px solid rgba(245,158,11,.3); }

    /* Skeleton */
    .fa-skeleton { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .fa-sk-panel { background: #0f1120; border-radius: 12px; height: 400px; animation: pulse 1.5s ease-in-out infinite alternate; }
    @keyframes pulse { from { opacity: .4; } to { opacity: .7; } }

    /* Paneles */
    .fa-panels {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
    }
    .fa-panel {
      background: #12152a;
      border-radius: 14px;
      padding: 16px;
      border: 1px solid #1e2235;
      display: flex; flex-direction: column; gap: 10px;
    }
    .fa-panel--postobon    { border-top: 3px solid #6366f1; }
    .fa-panel--competencia { border-top: 3px solid #ef4444; }

    /* Panel head */
    .fa-panel__head {
      display: flex; flex-direction: column; gap: 10px;
    }
    .fa-panel__label {
      display: flex; align-items: center; gap: 8px;
      font-size: 14px; font-weight: 700;
    }
    .fa-panel__dot {
      width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
    }
    .fa-panel__kpis {
      display: flex; gap: 8px;
    }
    .fa-kpi {
      flex: 1; background: #0f1120; border-radius: 8px;
      padding: 7px 10px; display: flex; flex-direction: column; align-items: center; gap: 2px;
      span  { font-size: 10px; color: #64748b; }
      strong{ font-size: 16px; font-weight: 700; color: #e2e8f0; }
    }

    /* Section titles */
    .fa-section-title {
      font-size: 11px; font-weight: 600; color: #64748b;
      text-transform: uppercase; letter-spacing: .05em;
      margin-top: 4px;
    }

    /* Company bars */
    .fa-company-bars { display: flex; flex-direction: column; gap: 5px; }
    .fa-company-row {
      display: grid; grid-template-columns: 120px 1fr 70px;
      align-items: center; gap: 8px;
    }
    .fa-company-name { font-size: 12px; color: #cbd5e1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .fa-company-name--alert { color: #f87171; font-weight: 600; }
    .fa-bar-track { background: #1e2235; border-radius: 4px; height: 8px; overflow: hidden; }
    .fa-bar-fill  { height: 100%; border-radius: 4px; transition: width .4s; }
    .fa-bar-val   { font-size: 11px; color: #94a3b8; text-align: right; white-space: nowrap; }
    .fa-bar-val--alert { color: #f87171; font-weight: 600; }
    .fa-bar-val small { color: #64748b; }

    /* Category list */
    .fa-category-list { display: flex; flex-direction: column; gap: 4px; }
    .fa-cat-row {
      display: grid; grid-template-columns: 10px 1fr 60px 36px 32px;
      align-items: center; gap: 6px;
    }
    .fa-cat-dot   { width: 8px; height: 8px; border-radius: 50%; }
    .fa-cat-name  { font-size: 11px; color: #94a3b8; }
    .fa-cat-track { background: #1e2235; border-radius: 3px; height: 5px; overflow: hidden; }
    .fa-cat-fill  { height: 100%; border-radius: 3px; transition: width .4s; }
    .fa-cat-val   { font-size: 11px; color: #e2e8f0; font-weight: 600; text-align: right; }
    .fa-cat-count { font-size: 10px; color: #64748b; text-align: right; }

    /* Matrix */
    .fa-matrix-wrap { overflow-x: auto; border-radius: 8px; border: 1px solid #1e2235; }
    .fa-matrix {
      width: 100%; border-collapse: collapse; font-size: 11px;
      th, td { padding: 5px 8px; border-bottom: 1px solid #1e2235; text-align: center; white-space: nowrap; }
      th { color: #64748b; font-weight: 600; background: #0f1120; }
      td { color: #e2e8f0; }
      .fa-matrix__brand { text-align: left; color: #cbd5e1; font-weight: 500; min-width: 100px; }
      .fa-matrix__total { font-weight: 700; color: #f1f5f9; }
      .fa-matrix__zero  { color: #334155; }
    }

    /* Position grid */
    .fa-grid-wrap {
      display: flex; flex-direction: column; gap: 4px;
    }
    .fa-grid-labels-row {
      display: grid; grid-template-columns: repeat(3,1fr);
      margin-left: 40px;
      span { font-size: 10px; color: #64748b; text-align: center; }
    }
    .fa-grid-labels-col {
      display: none; /* se muestra via CSS grid row */
    }
    .fa-grid {
      display: grid; grid-template-columns: repeat(3,1fr); gap: 4px;
      margin-left: 40px;
    }
    .fa-grid-cell {
      aspect-ratio: 1.4;
      border-radius: 6px;
      display: flex; align-items: center; justify-content: center;
      font-size: 12px; font-weight: 700; color: rgba(255,255,255,.9);
      transition: background .3s;
      cursor: default;
    }

    /* Pricing */
    .fa-pricing-row { display: flex; gap: 8px; flex-wrap: wrap; }
    .fa-pricing-chip {
      display: flex; align-items: center; gap: 5px;
      background: #0f1120; border-radius: 20px; padding: 4px 10px;
      font-size: 11px; color: #94a3b8;
      i { color: #6366f1; }
      span { }
      strong { color: #e2e8f0; }
    }

    /* Vacíos */
    .fa-vacios-section {
      margin-top: 16px;
      background: rgba(245,158,11,.07);
      border: 1px solid rgba(245,158,11,.25);
      border-radius: 12px;
      overflow: hidden;
    }
    .fa-vacios-header {
      display: flex; align-items: center; gap: 10px;
      padding: 12px 16px;
      border-bottom: 1px solid rgba(245,158,11,.15);
      i { color: #f59e0b; font-size: 16px; }
    }
    .fa-vacios-count { margin-left: auto; font-size: 18px; font-weight: 700; color: #f59e0b; }
    .fa-vacios-body  { padding: 12px 16px; display: flex; gap: 20px; align-items: start; flex-wrap: wrap; }
    .fa-vacios-desc  { font-size: 12px; color: #94a3b8; margin: 0; flex: 1; min-width: 180px; }

    /* Empty */
    .fa-empty { text-align: center; color: #64748b; padding: 40px; }

    @media (max-width: 768px) {
      .fa-panels { grid-template-columns: 1fr; }
      .fa-company-row { grid-template-columns: 90px 1fr 50px; }
    }
  `],
})
export class FridgeAnalysisCardComponent implements OnChanges {

  @Input() config?: FridgeAnalysisCardConfig | null;
  @Input() loading = false;

  get contaminationAlert(): number {
    if (!this.config) return 0;
    const p = this.config.postobonPanel;
    const alien = p.companyDist.filter(c => !c.company.toLowerCase().includes('postobon'));
    const alienCount = alien.reduce((s, c) => s + c.count, 0);
    return p.totalFaces > 0 ? (alienCount / p.totalFaces) * 100 : 0;
  }

  ngOnChanges(): void {}

  companyColor(company: string, panelTag: string, isAlert: boolean): string {
    if (isAlert) return '#ef4444';
    const lc = company.toLowerCase();
    if (lc.includes('postobon'))        return '#6366f1';
    if (lc.includes('coca cola'))       return '#ef4444';
    if (lc.includes('pepsico'))         return '#3b82f6';
    if (lc.includes('bavaria'))         return '#f59e0b';
    if (lc.includes('central cervecera')) return '#8b5cf6';
    return '#64748b';
  }

  gridCellColor(value: number, max: number, accent: string): string {
    if (!value || !max) return '#0f1120';
    const alpha = 0.1 + (value / max) * 0.85;
    return accent + Math.round(alpha * 255).toString(16).padStart(2, '0');
  }

  gridLabel(row: number, col: number): string {
    const rows = ['Arriba', 'Medio', 'Abajo'];
    const cols = ['Izq', 'Centro', 'Der'];
    return `${rows[row]} ${cols[col]}`;
  }

  pricingIcon(tipo: string): string {
    if (tipo === 'Precio')  return 'pi-tag';
    if (tipo === 'Cenefa')  return 'pi-bookmark';
    if (tipo === 'Logo')    return 'pi-image';
    return 'pi-info-circle';
  }
}
